import intlTelInput from 'intl-tel-input';
import React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { getErrorMessage } from '../../../utils/errorMessage';
import { sendPhoneNumber } from '../../signup.api';
import { doctrineAnalytics } from '../../../utils/analyticsUtils';
import '../signup_form.scss';
import '../signup_card.scss';
import './intl/intl_tel_input.scss';
import { BrandButton } from '../../../components/button';
import { MaterialIcon } from '../../../components/material-icon/MaterialIcon';

type Props = Partial<RouteComponentProps> & {
  saveAndGetToNextStep: Function;
  children: React.ReactNode;
  source: string;
  sourcePage: string;
};

type State = {
  phoneNumber: string;
  error: string;
  disabled: boolean;
};

export class SignUpAskPhoneContent extends React.Component<Props, State> {
  static defaultProps = {
    children: null,
    source: 'signup-phone-modal',
    sourcePage: '',
    history: null,
  };

  state: State = {
    phoneNumber: '',
    error: '',
    disabled: true,
  };

  componentDidMount() {
    if (this.input) {
      this.iti = intlTelInput(this.input, {
        customPlaceholder: (selectedCountryPlaceholder, data) => {
          if (data.iso2 === 'fr') return 'Ex. 06 55 55 55 55';
          return `Ex. ${selectedCountryPlaceholder}`;
        },
        nationalMode: true,
        preferredCountries: ['fr'],
        utilsScript: '/static_js/vendor/utils.js',
      });
      this.iti.promise.then(() => {
        // intlTelInput is binded asynchronously to the DOM so it's possible that the input field is already filled
        if (this.input && this.input.value) {
          this.handleChange();
        }
        this.setState({ disabled: false });
      });

      // eventListener needed for asynchronous js function intlTelInput (deletion to replace by onChange in input leads to signup fail)
      this.input.addEventListener('change', this.handleChange);
    }
  }

  componentWillUnmount() {
    if (this.input) this.input.removeEventListener('change', this.handleChange);
  }

  sendPhoneNumber = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (this.state.disabled) return;
    this.setState({ disabled: true });
    try {
      const { phoneNumber } = this.state;
      const phoneNumberCleaned = phoneNumber.replace(/\s/g, '');
      await sendPhoneNumber(phoneNumberCleaned);
      doctrineAnalytics('Account SignUpAskPhoneValidated', {
        source: this.props.source,
        sourcePage: this.props.sourcePage,
      });
      this.setState({ disabled: false });
      this.props.saveAndGetToNextStep(phoneNumberCleaned);
    } catch (error) {
      this.setState({ disabled: false });
      if (error.response === 'BYPASS_PHONE_VERIFICATION') {
        // automatically validated because Esendex downtime is simulated
        if (this.props.history) {
          this.props.history.push('/');
        }
      } else if (error.response && error.response.data) {
        doctrineAnalytics('Account SignUpAskPhoneError', {
          error: error.response.data.error,
          source: this.props.source,
          sourcePage: this.props.sourcePage,
        });
        this.setState({
          error: getErrorMessage(error.response),
        });
      } else {
        throw error;
      }
    }
  };

  handleChange = () => {
    const phoneNumber = this.iti.getNumber();
    this.setState({ phoneNumber, error: '' });
  };

  input: HTMLInputElement | null = null;

  iti: any;

  render() {
    const { disabled, error } = this.state;
    return (
      <>
        <MaterialIcon name="phone_iphone" className="signup-card__icon" />
        <div className="signup-card__title">
          <FormattedMessage id="signup/routes/ask-phone/SignUpAskPhoneContent.tsx/1" />
        </div>
        <div className="signup-card__subtitle">
          <FormattedMessage id="signup/routes/ask-phone/SignUpAskPhoneContent.tsx/2" />
        </div>
        <form onSubmit={this.sendPhoneNumber}>
          <div className="signup-form__label">
            <FormattedMessage id="signup/routes/ask-phone/SignUpAskPhoneContent.tsx/4" />
          </div>
          <div className="signup-form__phone">
            <input
              ref={ref => {
                this.input = ref;
              }}
              className="signup-form__phone__input"
              id="phone"
              type="tel"
            />
          </div>
          {error && (
            <div className="form-error is-visible signup-form__error">
              {error}
            </div>
          )}
          <BrandButton type="submit" large expanded disabled={disabled}>
            <FormattedMessage id="signup/routes/ask-phone/SignUpAskPhoneContent.tsx/5" />
          </BrandButton>
        </form>
        {this.props.children}
      </>
    );
  }
}
